/**
 * MODULE: platform/browser/frontend/utils/browserCrmTasks.ts
 * PURPOSE: Default browser automation tasks when launching from CRM drawers.
 * RECENT: 2026-06-23 [2.2.1471] — **`buildCrmHomeBrowserContext`** includes account attention count and top signal.
 * RECENT_PRIOR: 2026-06-22 [2.2.1470] — buildMyAccountsPageBrowserContext and buildDashboardPageBrowserContext for portfolio-aware browser automation.
 */

import type { BrowserMissionId } from '../data/browserMissions';

export function buildCrmLinkedInTask(company: string, person?: string): string {
  const co = company.trim() || 'this company';
  const who = (person ?? '').trim();
  if (who) {
    return `On LinkedIn, find ${who} at ${co}. Summarize their recent posts and role, then suggest a personalized outreach angle.`;
  }
  return `On LinkedIn, search for ${co}. Summarize recent company posts and key leadership updates.`;
}

export function buildCrmSalesforceTask(recordLabel: string, fieldHint?: string): string {
  const label = recordLabel.trim() || 'the CRM record';
  const hint = (fieldHint ?? '').trim();
  if (hint) {
    return `In Salesforce, open ${label} and update ${hint}. Confirm the saved value on screen before finishing.`;
  }
  return `In Salesforce, open ${label}, review key fields, and log a follow-up activity note.`;
}

export function buildCrmBraznTask(summary: string): string {
  return `In Brazn CRM: ${summary.trim() || 'complete the highest-priority CRM task visible on this page.'}`;
}

export function buildCrmHomeBrowserContext(opts: {
  totalPipeline: number;
  activeDeals: number;
  accountsCount: number;
  dealsAtRisk?: number;
  currencyCode?: string;
  /** Accounts in the rep patch with at_risk, renewal, or stalled signals. */
  accountAttentionCount?: number;
  topAttentionAccount?: string;
  topAttentionSignal?: string;
}): string {
  const currency = (opts.currencyCode ?? 'USD').trim();
  const lines = [
    'Context: CRM Home',
    `Total pipeline (${currency}): ${opts.totalPipeline}`,
    `Active deals: ${opts.activeDeals}`,
    `Accounts: ${opts.accountsCount}`,
    `Deals at risk: ${opts.dealsAtRisk ?? 0}`,
  ];
  if (opts.accountAttentionCount != null && opts.accountAttentionCount > 0) {
    lines.push(`Accounts needing attention: ${opts.accountAttentionCount}`);
  }
  if (opts.topAttentionAccount?.trim()) {
    lines.push(
      `Top attention: ${opts.topAttentionAccount.trim()} (${(opts.topAttentionSignal ?? 'signal').replace(/_/g, ' ')})`,
    );
  }
  return lines.join('\n');
}

export function buildContactsPageBrowserContext(opts: {
  totalContacts: number;
  activeContacts: number;
  companiesCount: number;
  recentActivityCount?: number;
  filteredCount?: number;
}): string {
  const lines = [
    'Context: CRM Contacts list',
    `Total contacts: ${opts.totalContacts}`,
    `Active contacts: ${opts.activeContacts}`,
    `Distinct companies: ${opts.companiesCount}`,
    `Active in last 7 days: ${opts.recentActivityCount ?? 0}`,
  ];
  if (opts.filteredCount != null) {
    lines.push(`Filtered view count: ${opts.filteredCount}`);
  }
  return lines.join('\n');
}

export function buildAccountsPageBrowserContext(opts: {
  totalAccounts: number;
  filteredCount?: number;
  bookmarkedCount?: number;
}): string {
  const lines = [
    'Context: CRM Accounts list',
    `Total accounts: ${opts.totalAccounts}`,
  ];
  if (opts.filteredCount != null) {
    lines.push(`Filtered view count: ${opts.filteredCount}`);
  }
  if (opts.bookmarkedCount != null) {
    lines.push(`Bookmarked: ${opts.bookmarkedCount}`);
  }
  return lines.join('\n');
}

export function buildContactRowBrowserContext(contact: {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  title?: string;
  status: string;
  linkedInUrl?: string;
}): string {
  const name = `${contact.firstName} ${contact.lastName}`.trim() || contact.email || '—';
  const lines = [
    'Context: CRM Contact',
    `Name: ${name}`,
    `Email: ${contact.email || '—'}`,
    `Company: ${contact.companyName || '—'}`,
    `Title: ${(contact.title ?? '').trim() || '—'}`,
    `Status: ${contact.status}`,
  ];
  const linkedIn = (contact.linkedInUrl ?? '').trim();
  if (linkedIn) {
    lines.push(`LinkedIn URL: ${linkedIn}`);
  }
  return lines.join('\n');
}

export function buildAccountRowBrowserContext(account: {
  name: string;
  source?: string;
  score?: number;
  priorityTier?: string;
}): string {
  const score =
    account.score != null && !Number.isNaN(account.score) ? String(account.score) : '—';
  return [
    'Context: CRM Account',
    `Name: ${account.name}`,
    `Source: ${(account.source ?? '').trim() || '—'}`,
    `Lead score: ${score}`,
    `Priority: ${(account.priorityTier ?? '').trim() || '—'}`,
  ].join('\n');
}

export function buildContactsListLinkedInTask(): string {
  return 'Review contacts with stale activity and open the top contact on LinkedIn for personalized outreach research.';
}

export function buildAccountsListLinkedInTask(): string {
  return 'Open LinkedIn for the highest-priority account in this list and summarize recent company news and leadership updates.';
}

export function buildAccountsListBraznTask(): string {
  return 'Review accounts in Brazn CRM and open the account that needs the most attention based on score or stale activity.';
}

export function buildMyAccountsPageBrowserContext(opts: {
  territoryLabel: string;
  focusSignal?: string;
  portfolioScope?: 'my' | 'team' | 'all';
  viewMode?: 'flat' | 'rollup';
  accountCount: number;
  signalCounts?: {
    at_risk?: number;
    renewal?: number;
    stalled?: number;
    expansion?: number;
  };
  topAttention?: Array<{ name: string; signal: string; reason?: string }>;
  totalPipeline?: number;
  dealCount?: number;
  currencyCode?: string;
}): string {
  const scopeLabel =
    opts.portfolioScope === 'team'
      ? 'Team patch (managed reps)'
      : opts.portfolioScope === 'all'
        ? 'All accounts'
        : 'My accounts';
  const lines = [
    'Context: CRM My accounts portfolio',
    `Territory scope: ${opts.territoryLabel}`,
    `Portfolio scope: ${scopeLabel}`,
    `View: ${opts.viewMode === 'rollup' ? 'Corporate families (rollup)' : 'All rows'}`,
    `Focus filter: ${(opts.focusSignal ?? 'all').replace(/_/g, ' ')}`,
    `Accounts in view: ${opts.accountCount}`,
  ];
  const sc = opts.signalCounts;
  if (sc) {
    lines.push(
      `Signals — at risk: ${sc.at_risk ?? 0}, renewal: ${sc.renewal ?? 0}, stalled: ${sc.stalled ?? 0}, expansion: ${sc.expansion ?? 0}`,
    );
  }
  const top = opts.topAttention?.[0];
  if (top?.name) {
    lines.push(`Top attention: ${top.name} (${top.signal}${top.reason ? ` — ${top.reason}` : ''})`);
  }
  if (opts.dealCount != null) {
    lines.push(`Open deals in territory: ${opts.dealCount}`);
  }
  if (opts.totalPipeline != null) {
    const currency = (opts.currencyCode ?? 'USD').trim();
    lines.push(`Territory pipeline (${currency}): ${opts.totalPipeline}`);
  }
  return lines.join('\n');
}

export function buildDashboardPageBrowserContext(opts: {
  dashboardPersona: string;
  pipelineValue?: number;
  openDeals?: number;
  atRiskDeals?: number;
  attentionCount?: number;
  topAttentionName?: string;
  currencyCode?: string;
}): string {
  const currency = (opts.currencyCode ?? 'USD').trim();
  const lines = [
    'Context: Revenue dashboard',
    `Dashboard persona: ${opts.dashboardPersona}`,
    `Pipeline (${currency}): ${opts.pipelineValue ?? 0}`,
    `Open deals: ${opts.openDeals ?? 0}`,
    `At-risk deals: ${opts.atRiskDeals ?? 0}`,
  ];
  if (opts.attentionCount != null) {
    lines.push(`Accounts needing attention: ${opts.attentionCount}`);
  }
  const top = (opts.topAttentionName ?? '').trim();
  if (top) {
    lines.push(`Top attention account: ${top}`);
  }
  return lines.join('\n');
}

export function buildTerritoryPageBrowserContext(opts: {
  territoryLabel: string;
  regionFilter: string;
  totalPipeline: number;
  dealCount: number;
  accountCount: number;
  weightedPipeline?: number;
  currencyCode?: string;
}): string {
  const currency = (opts.currencyCode ?? 'USD').trim();
  const lines = [
    'Context: CRM Territory hub',
    `Territory scope: ${opts.territoryLabel}`,
    `Region filter: ${opts.regionFilter}`,
    `Open deals: ${opts.dealCount}`,
    `Accounts in view: ${opts.accountCount}`,
    `Total pipeline (${currency}): ${opts.totalPipeline}`,
  ];
  if (opts.weightedPipeline != null) {
    lines.push(`Weighted pipeline (${currency}): ${opts.weightedPipeline}`);
  }
  return lines.join('\n');
}

export function buildTerritoryListLinkedInTask(): string {
  return 'Open LinkedIn for the highest-priority account in this territory view and summarize recent company news and leadership updates.';
}

export function buildTerritoryListBraznTask(): string {
  return 'Review territory accounts in Brazn CRM and open the account that needs the most attention in this view.';
}

export function defaultMissionForCrmSurface(surface: 'deal' | 'contact' | 'account'): BrowserMissionId {
  if (surface === 'contact') return 'linkedin';
  if (surface === 'account') return 'linkedin';
  return 'salesforce';
}

export function buildCompanyPageBrowserContext(opts: {
  companyName: string;
  leadScore?: number;
  priorityTier?: string;
  linkedinUrl?: string;
  website?: string;
}): string {
  const score =
    opts.leadScore != null && !Number.isNaN(opts.leadScore) ? String(opts.leadScore) : '—';
  const lines = [
    'Context: Company / Account detail',
    `Company: ${opts.companyName.trim() || '—'}`,
    `Lead score: ${score}`,
    `Priority: ${(opts.priorityTier ?? '').trim() || '—'}`,
    `Website: ${(opts.website ?? '').trim() || '—'}`,
  ];
  const linkedIn = (opts.linkedinUrl ?? '').trim();
  if (linkedIn) {
    lines.push(`LinkedIn URL: ${linkedIn}`);
  }
  return lines.join('\n');
}

export function buildAccountDetailBrowserContext(opts: {
  name: string;
  subsidiaryCount?: number;
  contactCount?: number;
}): string {
  return [
    'Context: CRM Account detail',
    `Account: ${opts.name.trim() || '—'}`,
    `Subsidiaries: ${opts.subsidiaryCount ?? 0}`,
    `Contacts: ${opts.contactCount ?? 0}`,
  ].join('\n');
}

export function buildDealRowBrowserContext(deal: {
  name: string;
  accountName: string;
  stage: string;
  amount: number;
  closeDateISO?: string;
  probability?: number;
}): string {
  return [
    'Context: CRM Deal / Opportunity',
    `Deal: ${deal.name.trim() || '—'}`,
    `Account: ${deal.accountName.trim() || '—'}`,
    `Stage: ${deal.stage}`,
    `Amount: ${deal.amount}`,
    `Close date: ${(deal.closeDateISO ?? '').trim() || '—'}`,
    `Probability: ${deal.probability ?? '—'}%`,
  ].join('\n');
}

export function buildDealRowLinkedInTask(accountName: string, dealName: string): string {
  const account = accountName.trim() || 'the account';
  const deal = dealName.trim() || 'this deal';
  return `On LinkedIn, research ${account} for deal "${deal}". Summarize recent company posts and leadership updates, then suggest personalized next-step talking points.`;
}

export function buildDealRowSalesforceTask(dealName: string, accountName: string): string {
  const deal = dealName.trim() || 'the opportunity';
  const account = accountName.trim() || 'the account';
  return `In Salesforce, open opportunity "${deal}" for ${account} and log a follow-up activity with the current stage status.`;
}

export function normalizeExternalStartUrl(url: string | undefined | null): string | undefined {
  const trimmed = (url ?? '').trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** Resolve stored LinkedIn slug or URL to a full start URL for the remote browser. */
export function resolveLinkedInStartUrl(raw?: string | null): string | undefined {
  const t = (raw ?? '').trim();
  if (!t) return undefined;
  if (/^https?:\/\//i.test(t)) return t;
  if (/linkedin\.com/i.test(t)) return `https://${t.replace(/^\/+/, '')}`;
  return `https://www.linkedin.com/in/${encodeURIComponent(t.replace(/^\/+/, ''))}`;
}

export function buildCrmTwitterTask(companyOrPerson: string): string {
  const label = companyOrPerson.trim() || 'this account';
  return `On X (Twitter), search for recent mentions of ${label}. Summarize sentiment, key posts, and outreach angles.`;
}
